import type { Detail, Order } from '@/api/orders/orders-types'
import type { SkidListItem } from '@/api/skids/skids-types'
import {
  type PackageCode,
  collectSkidsFromOrders,
  formatShippingUnitName,
  formatSkidSize,
  packageCode
} from '@/utils/order-skids'

export interface PackingListLine {
  /** Stable identity for React keys — item code + description repeat across order lines. */
  key: string
  /** EBMS product id (`Detail.inven`). */
  inven: string
  descr: string
  /** Pieces of this item on this shipping unit. */
  quantity: number
  /** Order quantity for the whole line (`Detail.quan`), for context on the ticket. */
  ordered: number
  /** Shipped quantity from EBMS (`Detail.ship`). */
  shipped: number
  /** EBMS unit of measure (`EA`, `PC`, `SQ FT`…); `null` on ~20% of rows. */
  uom: string | null
  /** Item colour (`BONE WHITE`) — a unit often mixes colours, so each line carries its own. */
  color: string | null
  /** Piece length in inches (`Detail.heightd`). */
  length: number | null
  bundleCount: number
}

/** `ordered 6 · shipped 6 · 1 bundle`; stock items with no bundles drop the bundle part. */
export function formatLineCounts(line: Pick<PackingListLine, 'ordered' | 'shipped' | 'bundleCount'>) {
  const counts = `ordered ${line.ordered} · shipped ${line.shipped}`
  if (!line.bundleCount) return counts
  return `${counts} · ${line.bundleCount} bundle${line.bundleCount === 1 ? '' : 's'}`
}

/**
 * The shop's code for a shipping unit on paper. An LHB is a package (`PKG`) — a bundle
 * (`BDL`), box (`BX`) or coil (`CL`) when its `label` says which; skids are `SKD`.
 */
export type ShippingUnitKind = 'SKD' | PackageCode

function unitKind(skid: SkidListItem): ShippingUnitKind | null {
  if (skid.skid_type === 'SKI' || skid.skid_type === 'MOS') return 'SKD'
  // An unset — or not-yet-known — label still prints as a package, never as a bare unit.
  if (skid.skid_type === 'LHB') return packageCode(skid.label)
  return null
}

export interface PackingListUnit {
  /** The first box of a merged run (see `mergeIdenticalBoxes`). */
  skidId: number | null
  /** Every unit printed in this block: one, or each of the identical boxes merged into it. */
  skidIds: number[]
  /** `SKI-…`, `BDL-…`/`PKG-…` (display name); `null` for bundles not yet on a unit and for merged boxes. */
  label: string | null
  /** `null` for the not-packed unit and for types with no shop code (`OUT`). */
  kind: ShippingUnitKind | null
  /** Skid `48 x 120` or bundle `120` in inches; `null` when there's nothing to go on. */
  size: string | null
  weight: number | null
  lines: PackingListLine[]
}

/** Physical shipping units on the list — a block of merged boxes counts each box. */
export function countShippingUnits(units: PackingListUnit[]): number {
  return units.reduce((sum, unit) => sum + unit.skidIds.length, 0)
}

/** `Unit 3 of 20`, `Units 5–20 of 20` for merged boxes, `No shipping unit` when unpacked. */
export function formatUnitPosition(units: PackingListUnit[], unit: PackingListUnit): string {
  if (unit.skidId == null) return 'No shipping unit'
  const before = countShippingUnits(units.slice(0, units.indexOf(unit)))
  const total = countShippingUnits(units)
  const count = unit.skidIds.length
  return count > 1
    ? `Units ${before + 1}–${before + count} of ${total}`
    : `Unit ${before + 1} of ${total}`
}

/** `16 × BX` for merged boxes, else the unit's code; `null` when it has none. */
export function formatUnitKind(unit: PackingListUnit): string | null {
  return unit.skidIds.length > 1 ? `${unit.skidIds.length} × ${unit.kind}` : unit.kind
}

/**
 * Boxed products (M120, M48…) get a box per bundle, which listed one by one fill a page with
 * the same line. Boxes holding the same thing collapse into one `16 × BX` block, at the
 * place of the first; weights and pieces add up.
 */
function mergeIdenticalBoxes(units: PackingListUnit[]): PackingListUnit[] {
  const sameContents = (unit: PackingListUnit) => {
    if (unit.kind !== 'BX' || unit.lines.length !== 1) return null
    // `ordered`/`shipped` stand in for the order line: the merged line keeps the first's.
    const { inven, descr, color, length, uom, quantity, bundleCount, ordered, shipped } = unit.lines[0]
    return JSON.stringify([inven, descr, color, length, uom, quantity, bundleCount, ordered, shipped])
  }
  const merged = new Map<string, PackingListUnit>()
  const out: PackingListUnit[] = []
  for (const unit of units) {
    const key = sameContents(unit)
    const into = key == null ? undefined : merged.get(key)
    if (!into) {
      if (key != null) merged.set(key, unit)
      out.push(unit)
      continue
    }
    into.skidIds.push(...unit.skidIds)
    // A run of boxes has no one name, and its QR opens the whole order.
    into.label = null
    if (unit.weight != null) into.weight = (into.weight ?? 0) + unit.weight
    const line = into.lines[0]
    into.lines[0] = {
      ...line,
      quantity: line.quantity + unit.lines[0].quantity,
      bundleCount: line.bundleCount + unit.lines[0].bundleCount
    }
  }
  return out
}

/**
 * Packing list grouped by shipping unit — what Brett prints for pickups: how many units
 * there are and which items (and how many pieces) sit on each. Bundles with no `skid_id`
 * land in a trailing "not packed" unit so the ticket still accounts for the whole order —
 * and so do items with no bundles at all (stock items like CTA skip production, so they
 * never get one), at their ordered quantity.
 */
export function buildPackingList(order: Order, skids?: SkidListItem[]): PackingListUnit[] {
  const known = skids?.length ? skids : collectSkidsFromOrders([order])
  const skidById = new Map(known.map((s) => [s.id, s] as const))

  const units = new Map<number | null, PackingListUnit>()
  const lineKeys = new Map<string, PackingListLine>()

  /** The shipping unit for `skidId`, created on first use; `null` is "not packed yet". */
  const unitFor = (skidId: number | null) => {
    let unit = units.get(skidId)
    if (!unit) {
      const skid = skidId == null ? undefined : skidById.get(skidId)
      unit = {
        skidId,
        skidIds: skidId == null ? [] : [skidId],
        label: skidId == null ? null : skid ? formatShippingUnitName(skid) : `SKD-${skidId}`,
        // A skid missing from the order is labelled `SKD-<id>` above, so it reads as a skid.
        kind: skidId == null ? null : skid ? unitKind(skid) : 'SKD',
        size: skid ? formatSkidSize(skid) : null,
        weight: null,
        lines: []
      }
      units.set(skidId, unit)
    }
    return unit
  }

  /** A fresh line for `detail` on the unit keyed `skidId`; counts start at zero. */
  const lineFor = (detail: Detail, skidId: number | null): PackingListLine => ({
    key: `${skidId}|${detail.autoid}`,
    inven: detail.inven,
    descr: detail.descr ?? '',
    quantity: 0,
    ordered: detail.quan ?? 0,
    shipped: detail.ship ?? 0,
    uom: detail.unit_meas ?? null,
    color: detail.color?.trim() || null,
    length: detail.heightd ?? null,
    bundleCount: 0
  })

  for (const detail of order.details ?? []) {
    if (!detail.bundles?.length) {
      // No bundles to count, so the pieces are what EBMS ships on this order.
      unitFor(null).lines.push({ ...lineFor(detail, null), quantity: detail.ship ?? detail.quan ?? 0 })
      continue
    }

    for (const bundle of detail.bundles) {
      const skidId = bundle.skid_id ?? null
      const unit = unitFor(skidId)
      if (bundle.weight != null) unit.weight = (unit.weight ?? 0) + bundle.weight

      const lineKey = `${skidId}|${detail.autoid}`
      let line = lineKeys.get(lineKey)
      if (!line) {
        line = lineFor(detail, skidId)
        lineKeys.set(lineKey, line)
        unit.lines.push(line)
      }
      line.quantity += bundle.quantity ?? 0
      line.bundleCount += 1
    }
  }

  // A bundle is only as long as its longest piece — the same rule a skid's length follows,
  // worked out from what is on it, since nobody picks a width for a bundle.
  for (const unit of units.values()) {
    if (unit.kind !== 'BDL') continue
    const longest = Math.max(0, ...unit.lines.map((line) => line.length ?? 0))
    unit.size = longest > 0 ? String(Math.ceil(longest)) : null
  }

  return mergeIdenticalBoxes(
    Array.from(units.values()).sort((a, b) => {
      if (a.skidId == null) return 1
      if (b.skidId == null) return -1
      return a.skidId - b.skidId
    })
  )
}

// A civic (fire) number opening an address line: `9491`, `9491A`, `5-120` (unit-number).
const CIVIC_NUMBER = /^\d+[a-z]?(?:-\d+[a-z]?)?\b/i

// The road's first word: enough to match `Conc.` with `Concession`, and to tell another road apart.
const roadWord = (line: string) => line.match(/[a-z]+/i)?.[0].toLowerCase() ?? ''

/**
 * Routific's ship-to with EBMS's fire number put back. Routific's geocoder drops a number it
 * can't find yet (a new fire number isn't on the map), leaving the driver only the road.
 */
export function withCivicNumber(routific: string | null, ebms: string | null | undefined): string | null {
  if (!routific || CIVIC_NUMBER.test(routific)) return routific
  const line = ebms?.trim() ?? ''
  const civic = line.match(CIVIC_NUMBER)?.[0]
  if (!civic) return routific
  // EBMS's ship address is the customer's own on some orders: its number must not land on
  // Routific's different road.
  const ebmsRoad = roadWord(line.slice(civic.length))
  const routificRoad = roadWord(routific)
  const sameRoad =
    !ebmsRoad || !routificRoad || ebmsRoad.startsWith(routificRoad) || routificRoad.startsWith(ebmsRoad)
  return sameRoad ? `${civic} ${routific}` : routific
}
